// ─── homeBrief ──────────────────────────────────────────────────────────────
// Metrics for the Partner Brief layout on Home. Answers, in order:
//   1. How much is under our pen, in dollars?          → computeCapitalPosition
//   2. What needs a decision from a partner today?     → rankNeedsDecision
//   3. How are we trending, and where do deals die?    → computeWeekDeltas
//                                                        computeConversion
//
// Pure functions — every read happens in HomeDashboard and the results are
// passed down. Mirrors the reviewMetrics split so both surfaces derive their
// numbers the same way.

import { KILLED_STAGES } from "../constants/dealStages";

// Stages that count as an actively-worked deal: underwriting and past it.
// Screening and Outreach are deliberately NOT active — a deal we're looking
// at isn't a deal we're pursuing, and counting them inflated the dollar
// figure with deals nobody had underwritten. Tracking is passive monitoring.
// "Unsolicited Offer" is a parallel track (an LOI the Sourcing tool fired
// before a deal record existed), so it belongs to the outreach motion, not
// the active pipeline.
export const ACTIVE_STAGES = ["Underwriting", "LOI Submitted", "In Diligence"];

// Stages whose new-this-week entries roll into "Other New Deals" — the
// deals we saw and logged but aren't actively pursuing yet.
export const OTHER_NEW_STAGES = ["Tracking", "Screening"];

const DAY_MS = 86_400_000;
const num = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));

// Best available price for a deal: what we underwrote it at if we've done the
// work, otherwise the ask. Underwritten wins because it's our number, not the
// seller's.
export function dealValue(d) {
  return num(d.underwrittenPrice) ?? num(d.askingPrice) ?? 0;
}

// ── 1. Capital position ───────────────────────────────────────────
// equityPct / equityTarget come from app_settings so the assumption is
// visible and editable rather than hard-coded. equityTarget may be null —
// callers must render the "no target set" case rather than inventing one.
export function computeCapitalPosition(deals, { equityPct = 0.4, equityTarget = null } = {}) {
  const active = (deals || []).filter(d => ACTIVE_STAGES.includes(d.stage));

  let volume = 0, sf = 0, capWeightSum = 0, capWeight = 0, priced = 0;
  for (const d of active) {
    const v = dealValue(d);
    volume += v;
    sf += num(d.squareFeet) ?? 0;
    if (v > 0) priced++;
    const cap = num(d.capRate);
    if (cap != null && cap > 0 && v > 0) { capWeightSum += cap * v; capWeight += v; }
  }

  const offMarket = active.filter(d => !d.marketed).length;
  const equity = volume * equityPct;

  return {
    activeCount: active.length,
    volume,
    sf,
    equity,
    equityPct,
    equityTarget,
    // Null when no target is configured — the UI must not draw a progress bar.
    equityPctOfTarget: equityTarget && equityTarget > 0 ? (equity / equityTarget) * 100 : null,
    avgBasisPsf: sf > 0 && volume > 0 ? volume / sf : null,
    weightedCap: capWeight > 0 ? capWeightSum / capWeight : null,
    offMarketPct: active.length > 0 ? (offMarket / active.length) * 100 : null,
    // How many active deals carry no price at all — a caveat worth surfacing
    // rather than silently averaging over.
    unpricedCount: active.length - priced,
  };
}

// ── 2. Needs a decision ───────────────────────────────────────────
// Scores every active deal on how badly it needs a partner's attention, then
// returns the top N with a one-line reason. Signals, highest weight first:
//   • overdue call-for-offers date
//   • stale — no logged stage movement in 14+ days
//   • in a best-and-final round (outcome pending, price call needed)
//   • call-for-offers inside 7 days
//   • advanced stage (UW / LOI / DD) — simply matters more
// Ties break on deal value, so the bigger check surfaces first.
export function rankNeedsDecision(deals, activityLog, { limit = 3, now = new Date() } = {}) {
  const nowMs = now.getTime();

  // Last logged stage movement per deal, for the staleness signal.
  const lastMove = new Map();
  for (const e of activityLog || []) {
    if (e.action !== "stage_changed" || !e.entity_id) continue;
    const t = new Date(e.created_at).getTime();
    if (!Number.isFinite(t)) continue;
    const prev = lastMove.get(e.entity_id);
    if (prev == null || t > prev) lastMove.set(e.entity_id, t);
  }

  const scored = (deals || [])
    .filter(d => ACTIVE_STAGES.includes(d.stage))
    .map(d => {
      const reasons = [];
      let score = 0;

      const cfo = d.callForOffersDate ? new Date(d.callForOffersDate).getTime() : null;
      if (cfo != null && Number.isFinite(cfo)) {
        const days = Math.round((cfo - nowMs) / DAY_MS);
        if (days < 0) { score += 100; reasons.push({ tone: "urgent", text: `Call for offers passed ${Math.abs(days)}d ago` }); }
        else if (days <= 7) { score += 55; reasons.push({ tone: "warn", text: days === 0 ? "Call for offers is today" : `Call for offers in ${days}d` }); }
      }

      const moved = lastMove.get(d.id) ?? (d.createdAt ? new Date(d.createdAt).getTime() : null);
      const idleDays = moved != null ? Math.floor((nowMs - moved) / DAY_MS) : null;
      if (idleDays != null && idleDays >= 14) {
        score += 60 + Math.min(idleDays, 60);
        reasons.push({ tone: "urgent", text: `No movement in ${idleDays} days` });
      }

      if (d.madeBestAndFinal) {
        score += 50;
        reasons.push({ tone: "good", text: "In a best-and-final round" });
      }

      // Every candidate is already at Underwriting or past it, so depth is
      // the tiebreak rather than the gate.
      if (d.stage === "LOI Submitted") score += 20;
      if (d.stage === "In Diligence")  score += 30;

      return { deal: d, score, idleDays, reasons, value: dealValue(d) };
    })
    .sort((a, b) => (b.score - a.score) || (b.value - a.value));

  return scored.slice(0, limit);
}

// ── 3a. Week-over-week deltas ─────────────────────────────────────
// Buckets two activity windows the same way and diffs them, so a bare count
// never ships without a baseline. `metric` keys mirror the old weekly tiles.
export function computeWeekDeltas({ thisWeekActs, lastWeekActs, outreachThis, outreachLast }) {
  const bucket = (acts) => {
    const entered = {};
    for (const a of acts || []) {
      let target = null;
      if (a.action === "stage_changed") target = a.metadata?.new_stage;
      else if (a.action === "deal_created") target = a.metadata?.stage;
      if (target) entered[target] = (entered[target] || 0) + 1;
    }
    return entered;
  };

  const cur = bucket(thisWeekActs);
  const prev = bucket(lastWeekActs);

  // "LOIs Sent" is every LOI that went out, bifurcated by where it came
  // from: hand-crafted LOIs on pipeline deals (stage → LOI Submitted) vs
  // the unsolicited offers Sourcing fires off market (stage → Unsolicited
  // Offer). Both are real LOIs, so both count toward the headline — the
  // split tells you which motion generated them.
  const other = (b) => OTHER_NEW_STAGES.reduce((s, k) => s + (b[k] || 0), 0);
  // Sourcing LOIs: the stage is "Sourcing" now (it was "Outreach", and
  // "Unsolicited Offer" before that); every name counts.
  const sourced = (b) => (b["Sourcing"] || 0) + (b["Outreach"] || 0) + (b["Unsolicited Offer"] || 0);
  const loiTotal = (b) => (b["LOI Submitted"] || 0) + sourced(b);
  const mk = (label, value, priorValue, info, breakdown) => ({
    label,
    value,
    delta: priorValue == null ? null : value - priorValue,
    info: info || null,
    breakdown: breakdown || null,
  });

  return [
    mk("LOIs Sent", loiTotal(cur), loiTotal(prev),
       "Every LOI that went out this week, split by origin. Pipeline = a deal we underwrote and bid on. Sourcing = an unsolicited offer fired off-market before a deal record existed. Both are LOIs; only the Pipeline ones enter the active pipeline.",
       [
         { label: "pipeline", value: cur["LOI Submitted"]     || 0 },
         { label: "sourcing", value: sourced(cur) },
       ]),
    mk("New UW",    cur["Underwriting"]  || 0, prev["Underwriting"]  || 0),
    mk("Outreach",  outreachThis ?? 0,         outreachLast ?? null,
       "Total emails sent from Sourcing this week. The unsolicited offers among them also appear under LOIs Sent as the Sourcing half of that split."),
    mk("Other New Deals", other(cur), other(prev),
       "New Tracking + Screening deals this week. These aren't in the active pipeline yet — active means underwriting and past it — but they show we're seeing more than we're pursuing."),
  ];
}

// ── 3c. Active-pipeline trend ─────────────────────────────────────
// Weekly active-deal count for the last `weeks` weeks, reconstructed from the
// stage log so a deal killed last month still shows in the weeks it was live.
// Feeds the sparkline on the This Week panel.
export function computeActiveTrend(deals, activityLog, { weeks = 12, now = new Date() } = {}) {
  // Per-deal chronological stage timeline.
  const timeline = new Map();
  for (const e of activityLog || []) {
    const id = e.entity_id;
    if (!id) continue;
    const stage = e.action === "deal_created" ? e.metadata?.stage : e.metadata?.new_stage;
    if (!stage) continue;
    if (!timeline.has(id)) timeline.set(id, []);
    timeline.get(id).push({ at: new Date(e.created_at).getTime(), stage });
  }
  for (const list of timeline.values()) list.sort((a, b) => a.at - b.at);

  const stageAt = (deal, atMs) => {
    const created = deal.createdAt ? new Date(deal.createdAt).getTime() : null;
    if (created == null || created > atMs) return null;      // didn't exist yet
    let latest = null;
    for (const ev of timeline.get(deal.id) || []) {
      if (ev.at <= atMs) latest = ev.stage; else break;
    }
    return latest ?? deal.stage;                              // pre-log fallback
  };

  const points = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const at = now.getTime() - i * 7 * DAY_MS;
    let count = 0;
    for (const d of deals || []) {
      const s = stageAt(d, at);
      if (s && ACTIVE_STAGES.includes(s)) count++;
    }
    points.push(count);
  }
  const first = points[0] ?? 0;
  const last = points[points.length - 1] ?? 0;
  return { points, delta: last - first };
}

// ── 3b. Conversion ────────────────────────────────────────────────
// Max-stage-reached counts, so a killed deal still counts toward the deepest
// tier it actually got to. `preProxySummaries` rows fold their aggregate
// totals in so pre-April-2026 history isn't missing (all-time views only —
// see the overlap check below).
//
// Milestones are EXPLICIT STAGE SETS, not STAGE_ORDER rank comparisons.
// That matters because "Unsolicited Offer" sits at order 2 — nominally
// deeper than "Underwriting" at 3 — since it's an LOI that fired from
// Sourcing before a deal record existed. Ranking it as past-Underwriting
// counted every mass-outreach touch as an underwritten deal and produced a
// funnel that went 432 underwritten → 32 LOIs. Unsolicited Offer is a
// parallel track, not a rung: it counts in Sourced and nowhere else unless
// the deal genuinely moved into the pipeline afterwards.
//
// "Acquired" (a closed deal) went through diligence, so it sits at the same
// depth as "In Diligence" — the deepest tier the funnel reports.
// How deep into the funnel a given stage sits. Everything not listed —
// Tracking, Outreach, Unsolicited Offer, and the three terminal stages —
// is depth 0: present in the book but not at a funnel milestone.
const STAGE_DEPTH = {
  "Screening":     1,
  "Underwriting":  2,
  "LOI Submitted": 3,
  "In Diligence":  4,
  "Acquired":      4,
};
// No "screened" tier. Only 7 of 512 deals have ever logged a Screening
// stage, so the step differed from Underwritten by a handful of rows and
// carried no signal — everything we screen, we underwrite. It also made
// Sourced → Screened the permanent "biggest leak", which said nothing.
const DEPTH_FOR_TIER = { underwritten: 2, loi: 3, diligence: 4 };

// A stage the deal passed straight through in less time than this didn't
// really happen — it's a data-entry artifact. Bulk re-stages and migrations
// write a burst of transitions with the same timestamp, which would otherwise
// register as genuine occupancy: six deals showed as "in diligence over the
// last 60 days" purely because a cleanup pass on 2026-07-21 moved them
// through In Diligence into Passed within the same day. A stage the deal
// still holds always counts, however briefly it has held it.
export const MIN_DWELL_MS = 24 * 60 * 60 * 1000;

// Every stage a deal actually OCCUPIED during [start, end] — the stage it
// held when the window opened, plus every stage it moved into inside it and
// stayed in long enough to count.
//
// This is what makes a windowed view tell the truth: a deal that went into
// diligence months ago is still in diligence today, so it belongs in the
// last-7-days picture even though it wasn't created there. Move it out and it
// drops off once the window rolls past the change.
function stagesDuring(deal, timeline, startMs, endMs, nowMs) {
  const created = deal.createdAt ? new Date(deal.createdAt).getTime() : null;
  if (created == null || created > endMs) return [];        // didn't exist yet

  const events = timeline.get(deal.id) || [];
  const out = [];

  // Stage held at the moment the window opened (or at creation, if the deal
  // was born inside the window). This one is held across the boundary by
  // definition, so it needs no dwell check.
  let atStart = null;
  for (const ev of events) {
    if (ev.at <= Math.max(startMs, created)) atStart = ev.stage; else break;
  }
  // No events at or before that point — fall back to the current stage, the
  // same pre-activity-log approximation the rest of the app makes.
  out.push(atStart ?? deal.stage);

  // Every stage entered inside the window that the deal actually sat in.
  // Dwell runs to the next transition, or to now for the stage it still holds.
  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    if (ev.at <= startMs || ev.at > endMs) continue;
    const next = events[i + 1];
    const heldUntil = next ? next.at : nowMs;
    const isCurrent = !next;
    if (isCurrent || heldUntil - ev.at >= MIN_DWELL_MS) out.push(ev.stage);
  }
  return out;
}

export function computeConversion(deals, activityLog, preProxySummaries = [], dateRange = null) {
  const windowed = !!(dateRange && (dateRange.start || dateRange.end));
  const startMs = dateRange?.start ? dateRange.start.getTime() : -Infinity;
  const endMs   = dateRange?.end   ? dateRange.end.getTime()   :  Infinity;
  const nowMs = Date.now();

  // Per-deal chronological stage timeline, built once.
  const timeline = new Map();
  for (const e of activityLog || []) {
    const id = e.entity_id;
    if (!id) continue;
    const stage = e.action === "deal_created" ? e.metadata?.stage : e.metadata?.new_stage;
    if (!stage) continue;
    if (!timeline.has(id)) timeline.set(id, []);
    timeline.get(id).push({ at: new Date(e.created_at).getTime(), stage });
  }
  for (const list of timeline.values()) list.sort((a, b) => a.at - b.at);

  let sourced = 0, underwritten = 0, loi = 0, bestFinal = 0, diligence = 0;

  for (const d of deals || []) {
    const created = d.createdAt ? new Date(d.createdAt).getTime() : null;

    // Sourced stays an EVENT — deals we picked up in this window. The tiers
    // below are states, so a windowed view can legitimately show more deals
    // underwriting than sourced: old deals are still being worked.
    const sourcedHere = windowed
      ? created != null && created >= startMs && created <= endMs
      : true;
    if (sourcedHere) sourced++;

    // Deepest funnel stage this deal genuinely occupied inside the window.
    // All-time runs the same path with an unbounded window, so both views
    // apply the same dwell filter and can't disagree about a deal.
    const occupied = stagesDuring(d, timeline, startMs, endMs, nowMs);
    let depth = 0;
    for (const s of occupied) {
      depth = Math.max(depth, STAGE_DEPTH[s] || 0);
    }

    // A deal flagged as having made a best-and-final necessarily submitted an
    // LOI to get there, whatever its logged stage history says. That matters
    // because imported deals carry no stage events at all — the seven named
    // 2025 deals all sit at Lost with an empty timeline — so judging them on
    // stage depth alone silently dropped every one of them.
    const stagesSay = depth >= DEPTH_FOR_TIER.loi;
    const reachedLoi = stagesSay || !!d.madeBestAndFinal;

    // Fill every tier at or above the depth reached, so the funnel keeps its
    // shape — a deal in diligence counts as having been through LOI and UW.
    if (depth >= DEPTH_FOR_TIER.underwritten || d.madeBestAndFinal) underwritten++;
    if (reachedLoi)                                                loi++;
    if (depth >= DEPTH_FOR_TIER.diligence)                         diligence++;

    // Best & Final is a flag with no timestamp, so an all-time view simply
    // trusts it. A windowed view still has to age it out, and the honest test
    // is whether the deal was IN PLAY during the window: it held some
    // non-terminal stage, or it was killed inside the window (becoming
    // terminal is itself activity). A deal killed six months ago is not part
    // of the last thirty days.
    if (d.madeBestAndFinal) {
      if (!windowed) {
        bestFinal++;
      } else {
        const heldLive = occupied.some(s => !KILLED_STAGES.includes(s));
        const killedInWindow = d.killedAt
          ? (() => {
              const t = new Date(d.killedAt).getTime();
              return Number.isFinite(t) && t >= startMs && t <= endMs;
            })()
          : false;
        if (heldLive || killedInWindow) bestFinal++;
      }
    }
  }

  // Pre-Proxy aggregates only apply when the window actually reaches back
  // into their period — a "last 30 days" view must not inherit 2025.
  //
  // These rows carry the 2025 slide's "Presented To IC" figures, which is
  // what Best & Final was called then, so they feed that tier directly.
  // Anything that reached an LOI was necessarily underwritten first, so the
  // LOI count also lifts Underwritten — otherwise the aggregate would show
  // more LOIs than underwritings and break the funnel's shape.
  for (const row of preProxySummaries || []) {
    if (!periodOverlaps(row, dateRange)) continue;
    const c = row.counts || {};
    sourced += c.total || 0;
    for (const key of ["dead", "active"]) {
      const lane = c[key];
      if (!lane) continue;
      const laneLoi = (lane.loi_off || 0) + (lane.loi_on || 0);
      const laneBf  = (lane.bf_off  || 0) + (lane.bf_on  || 0);
      loi          += laneLoi;
      bestFinal    += laneBf;
      underwritten += Math.max(laneLoi, laneBf);
    }
  }

  const steps = [
    { key: "sourced",      label: "Sourced",      count: sourced },
    { key: "underwritten", label: "Underwritten", count: underwritten },
    { key: "loi",          label: "LOI",          count: loi },
    { key: "bestFinal",    label: "Best & Final", count: bestFinal },
    { key: "diligence",    label: "In Diligence", count: diligence },
  ];

  // Conversion from each step to the next, plus the weakest link.
  //
  // In a windowed view, Sourced is an event and Screened is a state, so the
  // ratio between them isn't a conversion and can exceed 100%. Skip that
  // pair when nominating a leak — the real bottlenecks live downstream where
  // both sides are states measured the same way.
  let worst = null;
  for (let i = 0; i < steps.length - 1; i++) {
    const from = steps[i].count, to = steps[i + 1].count;
    const pct = from > 0 ? (to / from) * 100 : null;
    steps[i].convToNext = pct;
    steps[i].comparable = !(windowed && i === 0);

    if (
      pct != null && from >= 5 && steps[i].comparable &&
      (worst == null || pct < worst.pct)
    ) {
      worst = { pct, fromLabel: steps[i].label, toLabel: steps[i + 1].label, lost: Math.max(0, from - to), from };
    }
  }

  const max = Math.max(1, ...steps.map(s => s.count));
  for (const s of steps) s.pctOfMax = (s.count / max) * 100;

  return { steps, worstLeak: worst, windowed };
}

// Does a pre_proxy_summaries row's period intersect the requested window?
// An all-time window (no bounds) always includes it.
function periodOverlaps(row, dateRange) {
  if (!dateRange || (!dateRange.start && !dateRange.end)) return true;
  const rs = row.period_start ? new Date(row.period_start).getTime() : -Infinity;
  const re = row.period_end   ? new Date(row.period_end).getTime()   :  Infinity;
  const fs = dateRange.start ? dateRange.start.getTime() : -Infinity;
  const fe = dateRange.end   ? dateRange.end.getTime()   :  Infinity;
  return rs <= fe && re >= fs;
}

// Day-window presets for the conversion filter on Home. `days: null` is
// all time, which is also what includes the pre-Proxy 2025 aggregate.
export const CONVERSION_RANGES = [
  { days: 7,    label: "7d" },
  { days: 30,   label: "30d" },
  { days: 60,   label: "60d" },
  { days: 90,   label: "90d" },
  { days: null, label: "All time" },
];

// Turn a preset's `days` into the { start, end } shape computeConversion
// wants. Null days → unbounded.
export function daysToRange(days, now = new Date()) {
  if (days == null) return null;
  return { start: new Date(now.getTime() - days * DAY_MS), end: now };
}

// Killed-deal counts, for context lines.
export function countKilled(deals) {
  return (deals || []).filter(d => KILLED_STAGES.includes(d.stage)).length;
}
